import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FaBriefcase, FaArrowLeft, FaEdit, FaPhone, FaFax, FaEnvelope,
  FaMapMarkerAlt, FaSave, FaTimes, FaUser, FaClock, FaPlus,
  FaTrash, FaMobileAlt
} from 'react-icons/fa';
import './ProbationParoleOfficeDetail.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const ProbationParoleOfficeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [office, setOffice] = useState(null);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editedOffice, setEditedOffice] = useState(null);
  const [showAddStaffModal, setShowAddStaffModal] = useState(false);
  const [newStaff, setNewStaff] = useState({
    name: '',
    title: '',
    department: '',
    phone: '',
    cell_phone: '',
    phone_ext: '',
    email: '',
    notes: ''
  });
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchOfficeDetails();
  }, [id]);

  const fetchOfficeDetails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/probation-parole/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setOffice(data.data);
        setEditedOffice(data.data);
        setStaff(data.data.staff || []);
        setError('');
      } else {
        setError('Probation/parole office not found');
      }
    } catch (err) {
      console.error('Error fetching office details:', err);
      setError('Failed to load office details');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveOfficeEdit = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/probation-parole/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(editedOffice)
      });

      const data = await response.json();

      if (data.success) {
        setOffice(data.data);
        setEditedOffice(data.data);
        setEditMode(false);
      } else {
        alert('Failed to update office');
      }
    } catch (err) {
      console.error('Error updating office:', err);
      alert('Failed to update office');
    }
  };

  const handleCancelEdit = () => {
    setEditedOffice(office);
    setEditMode(false);
  };

  const handleInputChange = (field, value) => {
    setEditedOffice(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddStaff = async () => {
    if (!newStaff.name || !newStaff.title) {
      alert('Name and title are required');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/probation-parole/${id}/staff`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newStaff)
      });

      const data = await response.json();

      if (data.success) {
        await fetchOfficeDetails();
        setNewStaff({
          name: '',
          title: '',
          department: '',
          phone: '',
          cell_phone: '',
          phone_ext: '',
          email: '',
          notes: ''
        });
        setShowAddStaffModal(false);
      } else {
        alert('Failed to add staff member');
      }
    } catch (err) {
      console.error('Error adding staff:', err);
      alert('Failed to add staff member');
    }
  };

  const handleStaffInputChange = (field, value) => {
    setNewStaff(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getOfficeTypeBadge = (officeType) => {
    if (!officeType) return null;

    const colors = {
      'Adult': '#2c3e50',
      'Federal': '#8e44ad',
      'Juvenile/Youth Court': '#16a085'
    };

    return (
      <span
        className="office-type-badge-large"
        style={{ backgroundColor: colors[officeType] || '#7f8c8d' }}
      >
        {officeType}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="pp-office-detail-container">
        <div className="loading">Loading office details...</div>
      </div>
    );
  }

  if (error || !office) {
    return (
      <div className="pp-office-detail-container">
        <div className="error-message">{error || 'Office not found'}</div>
        <button className="btn-back" onClick={() => navigate('/address-book/probation-parole')}>
          <FaArrowLeft /> Back to Probation & Parole Offices
        </button>
      </div>
    );
  }

  return (
    <div className="pp-office-detail-container">
      {/* Header */}
      <div className="page-header">
        <button className="btn-back" onClick={() => navigate('/address-book/probation-parole')}>
          <FaArrowLeft /> Back to Probation & Parole
        </button>

        <div className="header-title">
          <FaBriefcase size={28} />
          <div>
            <h1>{office.name}</h1>
            <div className="header-badges">
              {getOfficeTypeBadge(office.office_type)}
              {office.region && (
                <span className="region-badge-large">{office.region}</span>
              )}
            </div>
          </div>
        </div>

        {user.role === 'admin' && !editMode && (
          <button className="btn-edit-office" onClick={() => setEditMode(true)}>
            <FaEdit /> Edit Office Info
          </button>
        )}

        {editMode && (
          <div className="edit-actions">
            <button className="btn-save" onClick={handleSaveOfficeEdit}>
              <FaSave /> Save
            </button>
            <button className="btn-cancel" onClick={handleCancelEdit}>
              <FaTimes /> Cancel
            </button>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="content-layout">
        {/* Office Information Section */}
        <div className="office-info-section">
          <h2>Office Information</h2>

          {editMode ? (
            <div className="info-card-compact">
              <div className="compact-grid">
                <div className="compact-item full-width">
                  <label>Office Name</label>
                  <input
                    type="text"
                    value={editedOffice.name || ''}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                  />
                </div>

                <div className="compact-item">
                  <label>Office Type</label>
                  <select
                    value={editedOffice.office_type || 'Adult'}
                    onChange={(e) => handleInputChange('office_type', e.target.value)}
                  >
                    <option value="Adult">Adult</option>
                    <option value="Federal">Federal</option>
                    <option value="Juvenile/Youth Court">Juvenile/Youth Court</option>
                  </select>
                </div>

                <div className="compact-item">
                  <label>Region</label>
                  <input
                    type="text"
                    value={editedOffice.region || ''}
                    onChange={(e) => handleInputChange('region', e.target.value)}
                  />
                </div>

                <div className="compact-item">
                  <label>County</label>
                  <input
                    type="text"
                    value={editedOffice.county || ''}
                    onChange={(e) => handleInputChange('county', e.target.value)}
                  />
                </div>

                <div className="compact-item">
                  <label>City</label>
                  <input
                    type="text"
                    value={editedOffice.address_city || ''}
                    onChange={(e) => handleInputChange('address_city', e.target.value)}
                  />
                </div>

                <div className="compact-item full-width">
                  <label>Street Address</label>
                  <input
                    type="text"
                    value={editedOffice.address_street || ''}
                    onChange={(e) => handleInputChange('address_street', e.target.value)}
                  />
                </div>

                <div className="compact-item">
                  <label>State</label>
                  <input
                    type="text"
                    value={editedOffice.address_state || 'MT'}
                    onChange={(e) => handleInputChange('address_state', e.target.value)}
                  />
                </div>

                <div className="compact-item">
                  <label>ZIP Code</label>
                  <input
                    type="text"
                    value={editedOffice.address_zip || ''}
                    onChange={(e) => handleInputChange('address_zip', e.target.value)}
                  />
                </div>

                <div className="compact-item">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={editedOffice.phone || ''}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                  />
                </div>

                <div className="compact-item">
                  <label>Fax</label>
                  <input
                    type="tel"
                    value={editedOffice.fax || ''}
                    onChange={(e) => handleInputChange('fax', e.target.value)}
                  />
                </div>

                <div className="compact-item full-width">
                  <label>Email</label>
                  <input
                    type="email"
                    value={editedOffice.email || ''}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                  />
                </div>

                <div className="compact-item full-width">
                  <label>Office Hours</label>
                  <input
                    type="text"
                    value={editedOffice.office_hours || ''}
                    onChange={(e) => handleInputChange('office_hours', e.target.value)}
                    placeholder="e.g., Monday-Friday 8:00 AM - 5:00 PM"
                  />
                </div>

                <div className="compact-item full-width">
                  <label>Service Area</label>
                  <input
                    type="text"
                    value={editedOffice.service_area || ''}
                    onChange={(e) => handleInputChange('service_area', e.target.value)}
                  />
                </div>

                <div className="compact-item full-width">
                  <label>Administrative Assistant</label>
                  <input
                    type="text"
                    value={editedOffice.administrative_assistant || ''}
                    onChange={(e) => handleInputChange('administrative_assistant', e.target.value)}
                  />
                </div>

                <div className="compact-item full-width">
                  <label>Notes</label>
                  <textarea
                    value={editedOffice.notes || ''}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    rows="3"
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="info-card-compact">
              <div className="compact-grid">
                <div className="compact-item">
                  <label><FaMapMarkerAlt /> Address</label>
                  <span>
                    {office.address_street || 'N/A'}
                    <br />
                    {office.address_city || office.city}, {office.address_state || 'MT'} {office.address_zip}
                  </span>
                </div>

                <div className="compact-item">
                  <label>County</label>
                  <span>{office.county || 'N/A'}</span>
                </div>

                <div className="compact-item">
                  <label><FaPhone /> Phone</label>
                  <span>{office.phone || 'N/A'}</span>
                </div>

                <div className="compact-item">
                  <label><FaFax /> Fax</label>
                  <span>{office.fax || 'N/A'}</span>
                </div>

                <div className="compact-item full-width">
                  <label><FaEnvelope /> Email</label>
                  <span>
                    {office.email ? (
                      <a href={`mailto:${office.email}`}>{office.email}</a>
                    ) : 'N/A'}
                  </span>
                </div>

                {office.office_hours && (
                  <div className="compact-item full-width">
                    <label><FaClock /> Office Hours</label>
                    <span>{office.office_hours}</span>
                  </div>
                )}

                {office.service_area && (
                  <div className="compact-item full-width">
                    <label>Service Area</label>
                    <span className="service-area-text">{office.service_area}</span>
                  </div>
                )}

                {office.administrative_assistant && (
                  <div className="compact-item full-width">
                    <label>Administrative Assistant</label>
                    <span>{office.administrative_assistant}</span>
                  </div>
                )}
              </div>

              {office.notes && (
                <>
                  <div className="info-divider"></div>
                  <div className="compact-item full-width">
                    <label>Notes</label>
                    <p className="notes-text">{office.notes}</p>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Staff Section */}
        <div className="office-staff-section">
          <div className="staff-header">
            <h2>Staff Contacts</h2>
            {user.role === 'admin' && (
              <button className="btn-add-staff" onClick={() => setShowAddStaffModal(true)}>
                <FaPlus /> Add Staff
              </button>
            )}
          </div>

          <div className="staff-list">
            {staff.length === 0 ? (
              <div className="no-staff-info">
                <FaUser size={48} />
                <p>No staff contacts</p>
              </div>
            ) : (
              staff.map((member) => (
                <div key={member.id} className="staff-card">
                  <div className="staff-card-header">
                    <div className="staff-name-section">
                      <h3>{member.name}</h3>
                      {member.title && (
                        <span className="title-text">{member.title}</span>
                      )}
                      {member.department && (
                        <span className="department-badge">{member.department}</span>
                      )}
                    </div>
                  </div>

                  <div className="staff-contact-info">
                    {member.phone && (
                      <div className="staff-contact-item">
                        <FaPhone className="staff-icon" />
                        <span>
                          {member.phone}
                          {member.phone_ext && ` ext. ${member.phone_ext}`}
                        </span>
                      </div>
                    )}

                    {member.cell_phone && (
                      <div className="staff-contact-item">
                        <FaMobileAlt className="staff-icon" />
                        <span>{member.cell_phone}</span>
                      </div>
                    )}

                    {member.email && (
                      <div className="staff-contact-item">
                        <FaEnvelope className="staff-icon" />
                        <a href={`mailto:${member.email}`}>{member.email}</a>
                      </div>
                    )}

                    {member.notes && (
                      <div className="staff-notes">
                        {member.notes}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Add Staff Modal */}
      {showAddStaffModal && (
        <div className="modal-overlay" onClick={() => setShowAddStaffModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Staff Member</h2>
              <button className="modal-close" onClick={() => setShowAddStaffModal(false)}>
                <FaTimes />
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={newStaff.name}
                  onChange={(e) => handleStaffInputChange('name', e.target.value)}
                  placeholder="Enter staff member name"
                />
              </div>

              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={newStaff.title}
                  onChange={(e) => handleStaffInputChange('title', e.target.value)}
                  placeholder="e.g., Probation Officer, Supervisor"
                />
              </div>

              <div className="form-group">
                <label>Department</label>
                <input
                  type="text"
                  value={newStaff.department}
                  onChange={(e) => handleStaffInputChange('department', e.target.value)}
                  placeholder="e.g., Adult Services, Juvenile Services"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={newStaff.phone}
                    onChange={(e) => handleStaffInputChange('phone', e.target.value)}
                    placeholder="Office phone"
                  />
                </div>

                <div className="form-group">
                  <label>Extension</label>
                  <input
                    type="text"
                    value={newStaff.phone_ext}
                    onChange={(e) => handleStaffInputChange('phone_ext', e.target.value)}
                    placeholder="Ext."
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Cell Phone</label>
                <input
                  type="tel"
                  value={newStaff.cell_phone}
                  onChange={(e) => handleStaffInputChange('cell_phone', e.target.value)}
                  placeholder="Cell phone number"
                />
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={newStaff.email}
                  onChange={(e) => handleStaffInputChange('email', e.target.value)}
                  placeholder="Email address"
                />
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={newStaff.notes}
                  onChange={(e) => handleStaffInputChange('notes', e.target.value)}
                  placeholder="Additional notes..."
                  rows="3"
                />
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowAddStaffModal(false)}>
                Cancel
              </button>
              <button className="btn-save" onClick={handleAddStaff}>
                <FaPlus /> Add Staff Member
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProbationParoleOfficeDetail;
